/* numero.ts — a fronteira entre o número do usuário e o número da máquina.
 *
 * Este arquivo é a ÚNICA porta por onde texto vira número e número vira texto.
 * Motivo histórico: na versão anterior o app tinha `parseFloat` solto em umas
 * telas, `Number(...)` em outras e `toFixed(2).replace('.', ',')` em nove
 * lugares — o mesmo valor aparecia como "500000,00" numa tela e "500.000,00"
 * noutra, e um campo com "12.500" era lido como 12,5 num formulário e 12500 em
 * outro. Um único ponto de leitura e um único ponto de escrita eliminam a
 * classe inteira de bug.
 *
 * NADA aqui toca DOM, `document`, `window` ou I/O: são funções puras,
 * chamáveis no servidor (que recalcula) e no navegador (que dá resposta
 * instantânea enquanto o usuário digita).
 */
/** Texto exibido quando a grandeza não existe (não é zero: é ausência). */
export const SEM_VALOR = '—';
/* Formatadores memoizados no módulo.
 *
 * `Number.prototype.toLocaleString` constrói um Intl.NumberFormat NOVO a cada
 * chamada. Numa tabela de 12 meses × 5 colunas isso são 60 formatadores por
 * repintura. Instanciar uma vez aqui é de graça e some com o custo. */
const MOEDA_BRL = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
const MOEDA_SEM_SIMBOLO = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
/* O ICU separa "R$" do número com ESPAÇO NÃO-SEPARÁVEL (U+00A0) e a versão do
 * ICU embarcada varia entre Node e navegadores. Normalizar para espaço comum
 * deixa a saída estável e comparável em teste — e igual em toda tela, que é o
 * que o produto precisa. */
const normalizarEspacos = (s) => s.replace(/\u00A0/g, ' ');
/**
 * parseNum(valor): lê um número escrito por humano brasileiro.
 *
 * Heurística (a entrada humana é o caso dominante; a entrada de máquina já
 * chega como `number` e sai no primeiro `if`):
 *   - number finito → ele mesmo. NaN/Infinity → 0 (nunca deixamos um número
 *     não-finito entrar no motor: ele contaminaria toda conta rio abaixo).
 *   - remove tudo que não for dígito, vírgula, ponto ou "-".
 *   - TEM vírgula → vírgula é decimal e os pontos são milhar.
 *     "12.500,00" → 12500 ; "1.234.567,89" → 1234567.89
 *   - NÃO tem vírgula mas TEM ponto:
 *       * mais de um ponto ("1.234.567") → milhar → 1234567
 *       * um ponto e o último grupo tem exatamente 3 dígitos ("12.500") →
 *         milhar → 12500   ← é o que o brasileiro quis dizer
 *       * senão ("12.5", "12.55") → ponto é decimal → 12.5
 *   - qualquer coisa que não vire número → 0.
 *
 * A ambiguidade real é "1.500": pode ser mil e quinhentos (pt-BR) ou um vírgula
 * cinco (en-US). Escolhemos pt-BR porque o usuário digita em pt-BR; o caminho
 * de máquina (`number`) nem chega aqui.
 */
export function parseNum(valor) {
    if (typeof valor === 'number')
        return Number.isFinite(valor) ? valor : 0;
    if (valor === null || valor === undefined)
        return 0;
    let s = String(valor).replace(/[^\d,.\-]/g, '');
    if (!s)
        return 0;
    if (s.includes(',')) {
        // vírgula = decimal; pontos = milhar
        s = s.replace(/\./g, '').replace(',', '.');
    }
    else if (s.includes('.')) {
        const qtdPontos = (s.match(/\./g) ?? []).length;
        const ultimoGrupo = s.slice(s.lastIndexOf('.') + 1);
        if (qtdPontos > 1 || ultimoGrupo.length === 3) {
            s = s.replace(/\./g, '');
        }
        // senão: o ponto é decimal e o texto já está no formato que Number() lê
    }
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
}
/** True quando a grandeza existe e é utilizável numa conta. */
function ausente(v) {
    return v === null || v === undefined || !Number.isFinite(v);
}
/**
 * formatarMoeda(v): "R$ 1.234,56". Ausência vira "—", nunca "R$ 0,00" —
 * confundir "não sei" com "zero" é o erro que faz o usuário achar que o
 * sistema calculou algo quando ele só não preencheu o dado.
 */
export function formatarMoeda(v, vazio = SEM_VALOR) {
    if (ausente(v))
        return vazio;
    return normalizarEspacos(MOEDA_BRL.format(v));
}
/**
 * formatarMoedaSemSimbolo(v): "1.234,56". É o que vai DENTRO de um input de
 * dinheiro (o "R$" mora no rótulo do campo, não no valor digitado).
 */
export function formatarMoedaSemSimbolo(v, vazio = '') {
    if (ausente(v))
        return vazio;
    return normalizarEspacos(MOEDA_SEM_SIMBOLO.format(v));
}
/**
 * formatarPercentual(fracao): recebe FRAÇÃO (0.12) e escreve "12,0%".
 * É a forma que sai do motor — `lucroPct`, `mcPct`, `folgaPct` são frações.
 */
export function formatarPercentual(fracao, casas = 1, vazio = SEM_VALOR) {
    if (ausente(fracao))
        return vazio;
    return (fracao * 100).toFixed(casas).replace('.', ',') + '%';
}
/**
 * formatarPercentualPontos(pontos): recebe PONTOS PERCENTUAIS (12) e escreve
 * "12,0%". É a forma que o usuário CADASTRA — comissão, tributo, lucro alvo e
 * despesas extras são digitados em pontos, não em fração.
 *
 * As duas funções existem separadas de propósito: no app antigo havia um `pct`
 * que recebia fração e um `fmtPct` que recebia pontos, ambos chamados de "pct",
 * e trocá-los dava um erro de 100× que passava despercebido em valores baixos.
 */
export function formatarPercentualPontos(pontos, casas = 1, vazio = SEM_VALOR) {
    if (ausente(pontos))
        return vazio;
    return pontos.toFixed(casas).replace('.', ',') + '%';
}
/**
 * formatarNumero(v): número solto em texto corrido, em pt-BR ("5,4", "1.250").
 * Por padrão até 2 casas e sem casas forçadas: "6" continua "6", não "6,00".
 */
export function formatarNumero(v, opcoes = {}) {
    const vazio = opcoes.vazio ?? SEM_VALOR;
    if (ausente(v))
        return vazio;
    const casasMin = opcoes.casasMin ?? 0;
    const casasMax = opcoes.casasMax ?? Math.max(2, casasMin);
    return normalizarEspacos(formatadorNumero(casasMin, casasMax).format(v));
}
/* Cache de formatadores por par (min, max). Ver a nota do topo: construir um
 * Intl.NumberFormat por célula de tabela é o tipo de custo que só aparece
 * quando o relatório cresce. */
const CACHE_NUMERO = new Map();
function formatadorNumero(casasMin, casasMax) {
    const chave = casasMin + ':' + casasMax;
    let f = CACHE_NUMERO.get(chave);
    if (!f) {
        f = new Intl.NumberFormat('pt-BR', {
            minimumFractionDigits: casasMin,
            maximumFractionDigits: casasMax,
        });
        CACHE_NUMERO.set(chave, f);
    }
    return f;
}
/** formatarMultiplicador(2.5) → "2,50×" (usado em "seu preço é 2,5× o custo"). */
export function formatarMultiplicador(v, vazio = SEM_VALOR) {
    if (ausente(v))
        return vazio;
    return v.toFixed(2).replace('.', ',') + '×';
}
/**
 * formatarMoedaDigitacao(bruto): máscara de dinheiro que opera sobre TEXTO,
 * não sobre número — preservar o que a pessoa acabou de teclar é o que impede
 * o campo de brigar com o teclado.
 *
 * Duas regras, e as duas vêm de como o integrador digita preço:
 *
 * 1. O DÍGITO ENTRA NA PARTE INTEIRA, à frente da vírgula. É o oposto da
 *    máscara "de caixa eletrônico", em que 1-2-5-0 vira 12,50 e quem quer
 *    R$ 1.250,00 precisa de seis teclas. Quase todo valor deste app é redondo.
 * 2. OS CENTAVOS ESTÃO SEMPRE LÁ, em duas casas: "1250" já se lê "1.250,00".
 *    O campo mostra desde a primeira tecla o mesmo número que vai ser salvo,
 *    e ninguém precisa sair do campo para descobrir se digitou mil ou dez.
 *    Quem quer centavos anda para trás ou tecla a vírgula (o web faz o pulo).
 *
 * Sem nenhum dígito o texto é VAZIO, nunca "0,00": campo em branco é ausência
 * de dado, e ausência não é zero. É isso que permite apagar tudo.
 *
 * Vive aqui, e não no pacote de UI, porque é uma transformação texto→texto
 * pura: dá para testar sem navegador. Quem liga isso num <input> é o web.
 */
export function formatarMoedaDigitacao(bruto) {
    const texto = String(bruto ?? '');
    const virgula = texto.indexOf(',');
    const inteiro = (virgula === -1 ? texto : texto.slice(0, virgula)).replace(/\D/g, '');
    const centavos = (virgula === -1 ? '' : texto.slice(virgula + 1)).replace(/\D/g, '');
    if (!inteiro && !centavos)
        return '';
    // Zeros à esquerda somem; sem parte inteira o texto ganha o "0" que faz o
    // número ser lido ("0,50" e não ",50").
    const agrupado = (inteiro.replace(/^0+(?=\d)/, '') || '0').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return agrupado + ',' + centavos.slice(0, 2).padEnd(2, '0');
}
//# sourceMappingURL=numero.js.map